import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  CASH_OUT_MATRIX_MAX_LEVEL,
  CASH_OUT_STEP4_CASH_WALLET,
  CASH_OUT_STEP4_COP_PROGRAM,
  CASH_OUT_STEP4_MAIN_AFP_POINTS,
  CASH_OUT_STEP4_REWARDS,
  getCashOutMatrixLevelAmount,
  uplineCashOutEnrollmentIndexForMatrixFillSeq,
} from "../../config/cashOutMatrixConfig";
import { creditCashOutBox } from "./cashOutBoxWallet";
import {
  buildCashOutTransactionId,
  recordCashOutLedgerEntry,
} from "./cashOutLedger";
import { allocateCashOutMatrixFillSeq } from "./cashOutMatrixRouting";
import { creditRewardsRetirementWallet } from "./rewardsRetirementWallet";

async function getUserDocByUid(uid) {
  const snap = await getDocs(
    query(collection(db, "users"), where("uid", "==", uid))
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

export async function findActiveCashOutProgramByEnrollmentSeq(enrollmentSeq) {
  const snap = await getDocs(
    query(
      collection(db, "cashOutPrograms"),
      where("enrollmentSeq", "==", Number(enrollmentSeq))
    )
  );
  if (snap.empty) return null;
  const programs = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.status === "active" || p.status === "completed");
  programs.sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  );
  return programs[0];
}

async function findOpenCashOutMatrixPosition(programDocId, level) {
  const snap = await getDocs(
    query(
      collection(db, "cashOutMatrixPositions"),
      where("programDocId", "==", programDocId),
      where("level", "==", level),
      where("positionStatus", "==", "open")
    )
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

async function createCashOutLevelPositions(
  programDocId,
  programPublicId,
  userId,
  level
) {
  for (let i = 1; i <= 3; i += 1) {
    await addDoc(collection(db, "cashOutMatrixPositions"), {
      programDocId,
      programId: programPublicId,
      userId,
      level,
      positionIndex: i,
      positionStatus: "open",
      filledByUserId: null,
      createdAt: new Date().toISOString(),
    });
  }
}

async function creditCashOutCashWallet(userDocRef, userData, amount, meta) {
  const w = userData.cashOutCashWallet || {
    earned: 0,
    available: 0,
    withdrawn: 0,
  };
  await updateDoc(userDocRef, {
    cashOutCashWallet: {
      earned: (w.earned || 0) + amount,
      available: (w.available || 0) + amount,
      withdrawn: w.withdrawn || 0,
    },
    updatedAt: new Date().toISOString(),
  });
  await recordCashOutLedgerEntry({
    transactionId: buildCashOutTransactionId(),
    programId: meta.programId,
    userId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "CASH_WALLET",
    fromWallet: "COP_MATRIX",
    toWallet: "CASH_OUT_CASH_WALLET",
    matrixPositionId: meta.matrixPositionId || null,
    fillerUserId: meta.fillerUserId || null,
    reference: meta.reference,
    metadata: meta.metadata || null,
  });
}

async function creditMainAutoFillPoints(userDocRef, userData, amount, meta) {
  const points = userData.autoFillPoints || {};
  await updateDoc(userDocRef, {
    autoFillPoints: {
      ...points,
      earned: (points.earned || 0) + amount,
      available: (points.available || 0) + amount,
      withdrawn: points.withdrawn || 0,
      locked: points.locked || 0,
    },
    updatedAt: new Date().toISOString(),
  });
  await recordCashOutLedgerEntry({
    transactionId: buildCashOutTransactionId(),
    programId: meta.programId,
    userId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "MAIN_AFP_POINTS",
    fromWallet: "COP_MATRIX",
    toWallet: "AUTO_FILL_POINTS",
    matrixPositionId: meta.matrixPositionId || null,
    fillerUserId: meta.fillerUserId || null,
    reference: meta.reference,
    metadata: meta.metadata || null,
  });
}

async function creditCashOutMatrixEarnings(userId, amount, meta) {
  return creditCashOutBox(userId, amount, meta);
}

async function creditCashOutUpgradePool(userDocRef, userData, amount, meta) {
  const upgrade = userData.cashOutUpgrade || { reserved: 0, used: 0 };
  await updateDoc(userDocRef, {
    cashOutUpgrade: {
      reserved: (upgrade.reserved || 0) + amount,
      used: upgrade.used || 0,
    },
    updatedAt: new Date().toISOString(),
  });
  await recordCashOutLedgerEntry({
    transactionId: buildCashOutTransactionId(),
    programId: meta.programId,
    userId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "LEVEL_UPGRADE",
    fromWallet: "COP_MATRIX",
    toWallet: "COP_UPGRADE",
    matrixPositionId: meta.matrixPositionId || null,
    fillerUserId: meta.fillerUserId || null,
    reference: meta.reference,
    metadata: meta.metadata || null,
  });
}

async function distributeStep4Fill(userDoc, program, levelAmount, meta) {
  const userRef = doc(db, "users", userDoc.id);
  await creditCashOutCashWallet(
    userRef,
    userDoc.data,
    CASH_OUT_STEP4_CASH_WALLET,
    meta
  );
  await creditMainAutoFillPoints(
    userRef,
    userDoc.data,
    CASH_OUT_STEP4_MAIN_AFP_POINTS,
    meta
  );
  await creditCashOutMatrixEarnings(
    program.userId,
    CASH_OUT_STEP4_COP_PROGRAM,
    {
      sourceProgramId: program.programId,
      cartOrderId: meta.reference,
      ...meta,
    }
  );
  await creditRewardsRetirementWallet(CASH_OUT_STEP4_REWARDS, {
    programId: program.programId,
    userId: program.userId,
    level: CASH_OUT_MATRIX_MAX_LEVEL,
    fillerUserId: meta.fillerUserId,
    reference: meta.reference,
    metadata: { kind: "COP_STEP4_FILL", levelAmount },
  });
}

async function tryAdvanceCashOutProgramLevel(programDocId, program, userDoc) {
  const currentLevel = program.currentLevel || 1;
  if (currentLevel >= CASH_OUT_MATRIX_MAX_LEVEL) {
    await updateDoc(doc(db, "cashOutPrograms", programDocId), {
      status: "completed",
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return { advanced: false, completed: true };
  }

  const nextLevel = currentLevel + 1;
  const activationCost = getCashOutMatrixLevelAmount(currentLevel) * 2;
  const upgrade = userDoc.data.cashOutUpgrade || {};
  if ((upgrade.reserved || 0) < activationCost) {
    return { advanced: false, reason: "insufficient_upgrade" };
  }

  await updateDoc(doc(db, "users", userDoc.id), {
    cashOutUpgrade: {
      reserved: (upgrade.reserved || 0) - activationCost,
      used: (upgrade.used || 0) + activationCost,
    },
    updatedAt: new Date().toISOString(),
  });

  await updateDoc(doc(db, "cashOutPrograms", programDocId), {
    currentLevel: nextLevel,
    levelPaymentsInCycle: 0,
    updatedAt: new Date().toISOString(),
  });

  await createCashOutLevelPositions(
    programDocId,
    program.programId,
    program.userId,
    nextLevel
  );

  await recordCashOutLedgerEntry({
    transactionId: buildCashOutTransactionId(),
    programId: program.programId,
    userId: program.userId,
    level: nextLevel,
    amount: activationCost,
    transactionType: "LEVEL_ACTIVATED",
    fromWallet: "COP_UPGRADE",
    toWallet: "LEVEL",
    reference: `COP activated level ${nextLevel}`,
  });

  return { advanced: true, newLevel: nextLevel };
}

/**
 * One downline enrollment / matrix event credits the upline COP program.
 */
export async function processMatrixFillForCashOutUpline({
  uplineProgram,
  uplineProgramDocId,
  fillerUserId,
  fillerProgramId,
  orderId,
  matrixFillSeq = null,
}) {
  const level = uplineProgram.currentLevel || 1;
  const position = await findOpenCashOutMatrixPosition(
    uplineProgramDocId,
    level
  );
  if (!position) return { filled: false, reason: "no_open_position" };

  const levelAmount = getCashOutMatrixLevelAmount(level);
  const userDoc = await getUserDocByUid(uplineProgram.userId);
  if (!userDoc) return { filled: false, reason: "upline_user_missing" };

  await updateDoc(doc(db, "cashOutMatrixPositions", position.id), {
    positionStatus: "filled",
    filledByUserId: fillerUserId,
    filledByProgramId: fillerProgramId,
    filledAt: new Date().toISOString(),
    orderId: orderId || null,
    matrixFillSeq: matrixFillSeq ?? null,
  });

  const paymentsInCycle = (uplineProgram.levelPaymentsInCycle || 0) + 1;
  const paymentIndex = ((paymentsInCycle - 1) % 3) + 1;
  const meta = {
    programId: uplineProgram.programId,
    userId: uplineProgram.userId,
    level,
    matrixPositionId: position.id,
    fillerUserId,
    reference: orderId,
  };

  const isFinalLevel = level >= CASH_OUT_MATRIX_MAX_LEVEL;

  if (isFinalLevel) {
    await distributeStep4Fill(userDoc, uplineProgram, levelAmount, meta);
  } else if (paymentIndex === 1) {
    await creditCashOutMatrixEarnings(
      uplineProgram.userId,
      levelAmount,
      {
        sourceProgramId: uplineProgram.programId,
        cartOrderId: orderId,
      }
    );
    await recordCashOutLedgerEntry({
      transactionId: buildCashOutTransactionId(),
      ...meta,
      amount: levelAmount,
      transactionType: "CASH_OUT_EARNINGS",
      fromWallet: "COP_MATRIX",
      toWallet: "CASH_OUT_BOX",
    });
  } else {
    const userRef = doc(db, "users", userDoc.id);
    await creditCashOutUpgradePool(userRef, userDoc.data, levelAmount, meta);
  }

  const programUpdate = {
    levelPaymentsInCycle: paymentsInCycle,
    updatedAt: new Date().toISOString(),
  };
  await updateDoc(doc(db, "cashOutPrograms", uplineProgramDocId), programUpdate);

  const refreshed = { ...uplineProgram, ...programUpdate };

  if (paymentsInCycle >= 3) {
    await updateDoc(doc(db, "cashOutPrograms", uplineProgramDocId), {
      levelPaymentsInCycle: 0,
      updatedAt: new Date().toISOString(),
    });
    const freshUser = await getUserDocByUid(uplineProgram.userId);
    await tryAdvanceCashOutProgramLevel(
      uplineProgramDocId,
      refreshed,
      freshUser
    );
  }

  return { filled: true, paymentIndex, levelAmount, level };
}

export async function applyCashOutMatrixEnrollmentLeg({
  userId,
  programId,
  cartOrderId,
}) {
  const fillSeq = await allocateCashOutMatrixFillSeq();
  const uplineIndex = uplineCashOutEnrollmentIndexForMatrixFillSeq(fillSeq);
  const upline = uplineIndex
    ? await findActiveCashOutProgramByEnrollmentSeq(uplineIndex)
    : null;

  let matrixFill = { applied: false, fillSeq, uplineIndex };
  if (upline) {
    const result = await processMatrixFillForCashOutUpline({
      uplineProgram: upline,
      uplineProgramDocId: upline.id,
      fillerUserId: userId,
      fillerProgramId: programId,
      orderId: cartOrderId,
      matrixFillSeq: fillSeq,
    });
    matrixFill = {
      ...matrixFill,
      applied: result.filled === true,
      reason: result.filled ? null : result.reason || "unknown",
      uplineProgramId: upline.programId,
      paymentIndex: result.paymentIndex,
      levelAmount: result.levelAmount,
      level: result.level,
    };
  } else {
    matrixFill.reason = "upline_program_missing";
  }

  return matrixFill;
}

export async function initializeCashOutProgramMatrix(
  programDocId,
  programPublicId,
  userId
) {
  await createCashOutLevelPositions(
    programDocId,
    programPublicId,
    userId,
    1
  );
}
